import * as React from "react";
import { WorkflowPropValue, WorkflowNode } from "../../types";
import { FormGroup, ToggleGroup } from "../RightAsideHelpers";
import Input from "@/components/Input";
import { TertiaryGrayButton } from "@velaro/velaro-component-lib";
import { IconPlus } from "@velaro/velaro-component-lib";
import { IconClose } from "@velaro/velaro-component-lib";
import VariableTextInput from "@/components/VariableTextInput";
import VariableHelpCard from "@/components/VariableHelpCard";
import FieldNamePicker from "@/components/FieldNamePicker";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function SetDataEditor({ node, onUpdate }: Props) {
  const activeScopes = [
    node.saveAsContactProp && "contact",
    node.saveAsConversationProp && "conversation",
    node.saveAsWorkflowProp && "workflow"
  ].filter(Boolean) as string[];

  return (
    <>
      <VariableHelpCard context="setData" />
      <FormGroup label="Data to set">
        <div className="border rounded-lg p-2 mb-4 bg-slate-gray-100 flex flex-col items-center">
          {(node.dataToSet || []).map((data, i) => (
            <React.Fragment key={i}>
              <div className="flex w-full items-center gap-1 my-2">
                <div className="flex-1 min-w-0">
                  <FieldNamePicker
                    value={data.name}
                    onChange={(name) => {
                      const newData = [...node.dataToSet];
                      newData[i].name = name;
                      onUpdate({ dataToSet: newData });
                    }}
                    scopes={activeScopes.length > 0 ? activeScopes : undefined}
                    placeholder="Which variable to set?"
                  />
                </div>
                <IconClose
                  onClick={() => {
                    onUpdate({
                      dataToSet: node.dataToSet.filter(
                        (_, index) => index !== i
                      )
                    });
                  }}
                  className="cursor-pointer"
                  style={{ color: "#CBD5E1" }}
                />
              </div>
              <VariableTextInput
                placeholder="Value (type @ to insert a variable)"
                className="my-2"
                value={data.value}
                onChange={(value) => {
                  const newData = [...node.dataToSet];
                  newData[i].value = value;
                  onUpdate({ dataToSet: newData });
                }}
              />
              <div className="my-2 border border-slate-300 w-full"></div>
            </React.Fragment>
          ))}
          <TertiaryGrayButton
            label="Add Data"
            prefixIcon={<IconPlus />}
            className="w-full"
            onClick={() => {
              const newProp = {
                name: "",
                value: ""
              } as WorkflowPropValue;
              onUpdate({ dataToSet: [...(node.dataToSet || []), newProp] });
            }}
          />
        </div>
      </FormGroup>
      <FormGroup label="Save as">
        <div className="mb-3 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-slate-600">
          <span className="font-semibold text-slate-700">Which should I use?</span>
          <ul className="mt-1 space-y-0.5">
            <li>→ Need it next time they chat? <span className="font-medium">Save permanently</span></li>
            <li>→ Only needed for today's chat? <span className="font-medium">This chat only</span></li>
            <li>→ Just doing a calculation right now? <span className="font-medium">Step only</span></li>
          </ul>
        </div>
        <ToggleGroup
          toggled={node.saveAsContactProp || false}
          label="Save permanently (to their profile)"
          helpText="Remembered across ALL future conversations with this person. Even if they come back weeks later, this value is still there. Use for: email, name, subscription plan, company, any preference they've told you once."
          changeToggle={() => {
            onUpdate({
              saveAsContactProp: !node.saveAsContactProp
            });
          }}
        />
        <ToggleGroup
          toggled={node.saveAsConversationProp || false}
          label="This chat only"
          helpText="Available throughout this conversation across all workflow steps — but cleared when the chat ends. Use for: what brought them in today, their order number for this session, the issue they're reporting right now."
          changeToggle={() => {
            onUpdate({
              saveAsConversationProp: !node.saveAsConversationProp
            });
          }}
        />
        <ToggleGroup
          toggled={node.saveAsWorkflowProp || false}
          label="This step only (advanced)"
          helpText="Temporary scratch space — gone the moment this workflow finishes. Nothing is stored anywhere. Use for: intermediate counters, loop variables, calculations you only need inside this one workflow."
          changeToggle={() => {
            onUpdate({
              saveAsWorkflowProp: !node.saveAsWorkflowProp
            });
          }}
        />
      </FormGroup>
    </>
  );
}
